import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

function DeliveryAssignmentTracker() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    assignmentStatus: '',
    status: ''
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.assignmentStatus && { assignmentStatus: filters.assignmentStatus }),
        ...(filters.status && { status: filters.status })
      };

      const response = await adminAPI.getDeliveryAssignmentTracker(params);
      if (response.data.success) {
        setOrders(response.data.data.orders);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total,
          pages: response.data.data.pagination.pages
        }));
        setStats(response.data.data.stats);
      }
    } catch (err) {
      setError('Failed to fetch delivery assignments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchOrderDetails = async (orderId) => {
    try {
      setDetailsLoading(true);
      const response = await adminAPI.getOrderAssignmentDetails(orderId);
      if (response.data.success) {
        setOrderDetails(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    fetchOrderDetails(order._id);
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
  };

  const getAssignmentStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#9e9e9e', text: 'Pending' },
      batch1_sent: { bg: '#2196f3', text: 'Batch 1 Sent' },
      batch2_sent: { bg: '#ff9800', text: 'Batch 2 Sent' },
      assigned: { bg: '#4caf50', text: 'Assigned' },
      auto_assigned: { bg: '#9c27b0', text: 'Auto-Assigned' },
      failed: { bg: '#f44336', text: 'Failed' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span style={{
        background: badge.bg,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {badge.text}
      </span>
    );
  };

  const getResponseBadge = (response) => {
    const colors = {
      pending: '#9e9e9e',
      accepted: '#4caf50',
      rejected: '#f44336',
      expired: '#ff9800'
    };
    return (
      <span style={{
        background: colors[response] || '#9e9e9e',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: '600'
      }}>
        {response?.toUpperCase() || 'PENDING'}
      </span>
    );
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Navbar />
      <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '20px' }}>Delivery Assignment Tracker</h1>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Visualize the 7+7+Random delivery assignment system
        </p>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div style={{ background: '#2196f3', color: 'white', padding: '20px', borderRadius: '10px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.batch1_sent || 0}</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Batch 1 Sent</div>
          </div>
          <div style={{ background: '#ff9800', color: 'white', padding: '20px', borderRadius: '10px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.batch2_sent || 0}</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Batch 2 Sent</div>
          </div>
          <div style={{ background: '#4caf50', color: 'white', padding: '20px', borderRadius: '10px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.assigned || 0}</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Manually Accepted</div>
          </div>
          <div style={{ background: '#9c27b0', color: 'white', padding: '20px', borderRadius: '10px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.auto_assigned || 0}</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Auto-Assigned</div>
          </div>
          <div style={{ background: '#f44336', color: 'white', padding: '20px', borderRadius: '10px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.failed || 0}</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Failed</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: 'white', padding: '15px', borderRadius: '10px', marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
          <select
            value={filters.assignmentStatus}
            onChange={(e) => setFilters({ ...filters, assignmentStatus: e.target.value })}
            style={{ padding: '8px 12px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="">All Assignment Status</option>
            <option value="batch1_sent">Batch 1 Sent</option>
            <option value="batch2_sent">Batch 2 Sent</option>
            <option value="assigned">Assigned</option>
            <option value="auto_assigned">Auto-Assigned</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            style={{ padding: '8px 12px', borderRadius: '5px', border: '1px solid #ddd' }}
          >
            <option value="">All Order Status</option>
            <option value="restaurant_confirmed">Restaurant Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready_for_pickup">Ready for Pickup</option>
            <option value="assigned_delivery">Assigned Delivery</option>
            <option value="accepted">Accepted</option>
            <option value="picked_up">Picked Up</option>
            <option value="on_the_way">On The Way</option>
            <option value="delivered">Delivered</option>
          </select>

          <button
            onClick={() => {
              setPagination(prev => ({ ...prev, page: 1 }));
              fetchOrders();
            }}
            style={{
              padding: '8px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Apply Filters
          </button>

          <button
            onClick={() => {
              setFilters({ assignmentStatus: '', status: '' });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{
              padding: '8px 20px',
              background: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
        </div>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: '15px', borderRadius: '10px', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : (
          <>
            {/* Orders Table */}
            <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Order ID</th>
                    <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Restaurant</th>
                    <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Customer</th>
                    <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Assignment Status</th>
                    <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Batch 1</th>
                    <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Batch 2</th>
                    <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Accepted By</th>
                    <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Created</th>
                    <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '15px' }}>
                        <div style={{ fontWeight: '600' }}>#{order.orderId}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{order.status}</div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div>{order.restaurant?.name || '-'}</div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div>{order.customer?.name || '-'}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{order.customer?.phone}</div>
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center' }}>
                        {getAssignmentStatusBadge(order.deliveryAssignment?.status)}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center' }}>
                        <div style={{
                          background: order.deliveryAssignment?.batch1?.length > 0 ? '#e3f2fd' : '#f5f5f5',
                          padding: '5px 10px',
                          borderRadius: '5px',
                          display: 'inline-block'
                        }}>
                          <span style={{ fontWeight: '600', color: '#1976d2' }}>
                            {order.deliveryAssignment?.batch1?.length || 0}
                          </span>
                          <span style={{ color: '#666', fontSize: '12px' }}> partners</span>
                        </div>
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center' }}>
                        <div style={{
                          background: order.deliveryAssignment?.batch2?.length > 0 ? '#fff3e0' : '#f5f5f5',
                          padding: '5px 10px',
                          borderRadius: '5px',
                          display: 'inline-block'
                        }}>
                          <span style={{ fontWeight: '600', color: '#f57c00' }}>
                            {order.deliveryAssignment?.batch2?.length || 0}
                          </span>
                          <span style={{ color: '#666', fontSize: '12px' }}> partners</span>
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        {order.deliveryAssignment?.acceptedBy ? (
                          <div>
                            <div style={{ fontWeight: '600', color: '#4caf50' }}>
                              {order.deliveryAssignment.acceptedBy.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {order.deliveryAssignment.acceptedBy.batch === 'auto' ? (
                                <span style={{ color: '#9c27b0' }}>Auto-assigned</span>
                              ) : (
                                `Batch ${order.deliveryAssignment.acceptedBy.batch}`
                              )}
                            </div>
                          </div>
                        ) : order.assignedPartner ? (
                          <div>
                            <div style={{ fontWeight: '600', color: '#4caf50' }}>
                              {order.assignedPartner.name}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '15px' }}>
                        {formatTime(order.createdAt)}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleOrderClick(order)}
                          style={{
                            padding: '6px 15px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {orders.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No orders found with delivery assignment data
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '8px 15px',
                    background: pagination.page === 1 ? '#f5f5f5' : '#667eea',
                    color: pagination.page === 1 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span style={{ padding: '8px 15px' }}>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  style={{
                    padding: '8px 15px',
                    background: pagination.page === pagination.pages ? '#f5f5f5' : '#667eea',
                    color: pagination.page === pagination.pages ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '15px',
              width: '90%',
              maxWidth: '1000px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 1
              }}>
                <div>
                  <h2 style={{ margin: 0 }}>Order #{selectedOrder.orderId}</h2>
                  <p style={{ margin: '5px 0 0', color: '#666' }}>Delivery Assignment Details</p>
                </div>
                <button
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  &times;
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '20px' }}>
                {detailsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>Loading details...</div>
                ) : orderDetails ? (
                  <>
                    {/* Order Info */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '20px', marginBottom: '30px' }}>
                      <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '10px' }}>
                        <h4 style={{ margin: '0 0 10px', color: '#666' }}>Restaurant</h4>
                        <div style={{ fontWeight: '600' }}>{orderDetails.restaurant?.name || '-'}</div>
                        <div style={{ fontSize: '13px', color: '#666' }}>{orderDetails.restaurant?.address?.street}</div>
                      </div>
                      <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '10px' }}>
                        <h4 style={{ margin: '0 0 10px', color: '#666' }}>Customer</h4>
                        <div style={{ fontWeight: '600' }}>{orderDetails.customer?.name || '-'}</div>
                        <div style={{ fontSize: '13px', color: '#666' }}>{orderDetails.customer?.phone}</div>
                      </div>
                      <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '10px' }}>
                        <h4 style={{ margin: '0 0 10px', color: '#666' }}>Assigned Partner</h4>
                        {orderDetails.assignedPartner ? (
                          <>
                            <div style={{ fontWeight: '600', color: '#4caf50' }}>{orderDetails.assignedPartner.name}</div>
                            <div style={{ fontSize: '13px', color: '#666' }}>{orderDetails.assignedPartner.phone}</div>
                          </>
                        ) : (
                          <div style={{ color: '#999' }}>Not assigned yet</div>
                        )}
                      </div>
                    </div>

                    {/* Assignment Flow Visualization */}
                    <h3 style={{ marginBottom: '15px' }}>Assignment Flow (7+7+Random)</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', gap: '20px', marginBottom: '30px' }}>
                      {/* Batch 1 */}
                      <div style={{
                        border: '2px solid #2196f3',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#2196f3',
                          color: 'white',
                          padding: '10px 15px',
                          fontWeight: '600'
                        }}>
                          Batch 1 (First 7 Partners)
                          {orderDetails.assignment?.batch1?.sentAt && (
                            <div style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                              Sent: {formatTime(orderDetails.assignment.batch1.sentAt)}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '10px', maxHeight: '300px', overflow: 'auto' }}>
                          {orderDetails.assignment?.batch1?.partners?.length > 0 ? (
                            orderDetails.assignment.batch1.partners.map((partner, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '10px',
                                  background: partner.response === 'accepted' ? '#e8f5e9' : '#f5f5f5',
                                  borderRadius: '5px',
                                  marginBottom: '8px',
                                  border: partner.response === 'accepted' ? '2px solid #4caf50' : 'none'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600' }}>{partner.name}</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {partner.distance} km | {partner.vehicleType}
                                  </div>
                                </div>
                                {getResponseBadge(partner.response)}
                              </div>
                            ))
                          ) : (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                              No partners in Batch 1
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Batch 2 */}
                      <div style={{
                        border: '2px solid #ff9800',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#ff9800',
                          color: 'white',
                          padding: '10px 15px',
                          fontWeight: '600'
                        }}>
                          Batch 2 (Next 7 Partners)
                          {orderDetails.assignment?.batch2?.sentAt && (
                            <div style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                              Sent: {formatTime(orderDetails.assignment.batch2.sentAt)}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '10px', maxHeight: '300px', overflow: 'auto' }}>
                          {orderDetails.assignment?.batch2?.partners?.length > 0 ? (
                            orderDetails.assignment.batch2.partners.map((partner, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '10px',
                                  background: partner.response === 'accepted' ? '#e8f5e9' : '#f5f5f5',
                                  borderRadius: '5px',
                                  marginBottom: '8px',
                                  border: partner.response === 'accepted' ? '2px solid #4caf50' : 'none'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600' }}>{partner.name}</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {partner.distance ? `${partner.distance.toFixed(2)} km` : '-'} | {partner.vehicleType}
                                  </div>
                                </div>
                                {getResponseBadge(partner.response)}
                              </div>
                            ))
                          ) : (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                              No partners in Batch 2
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Auto-Assignment */}
                      <div style={{
                        border: '2px solid #9c27b0',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#9c27b0',
                          color: 'white',
                          padding: '10px 15px',
                          fontWeight: '600'
                        }}>
                          Auto-Assignment (Random)
                          {orderDetails.assignment?.autoAssignment?.assignedAt && (
                            <div style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                              At: {formatTime(orderDetails.assignment.autoAssignment.assignedAt)}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '15px' }}>
                          {orderDetails.assignment?.autoAssignment ? (
                            <div>
                              <div style={{
                                background: '#f3e5f5',
                                padding: '15px',
                                borderRadius: '10px',
                                marginBottom: '10px'
                              }}>
                                <div style={{ fontWeight: '600', color: '#9c27b0', marginBottom: '5px' }}>
                                  Auto-Assigned To:
                                </div>
                                <div style={{ fontWeight: '600' }}>{orderDetails.assignedPartner?.name}</div>
                              </div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                <strong>Reason:</strong> {orderDetails.assignment.autoAssignment.reason}
                              </div>
                            </div>
                          ) : orderDetails.assignment?.status === 'assigned' ? (
                            <div style={{
                              background: '#e8f5e9',
                              padding: '15px',
                              borderRadius: '10px',
                              textAlign: 'center'
                            }}>
                              <div style={{ color: '#4caf50', fontWeight: '600' }}>
                                Manually Accepted
                              </div>
                              <div style={{ fontSize: '13px', marginTop: '5px' }}>
                                A delivery partner accepted the order before auto-assignment
                              </div>
                            </div>
                          ) : (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                              Not auto-assigned
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Timeline */}
                    <h3 style={{ marginBottom: '15px' }}>Assignment Timeline</h3>
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: '10px',
                      padding: '20px'
                    }}>
                      {orderDetails.timeline?.map((event, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            gap: '15px',
                            marginBottom: idx < orderDetails.timeline.length - 1 ? '20px' : 0
                          }}
                        >
                          <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: event.response === 'accepted' ? '#4caf50' :
                                       event.response === 'rejected' ? '#f44336' :
                                       event.event.includes('Auto') ? '#9c27b0' :
                                       event.event.includes('Batch 2') ? '#ff9800' :
                                       '#2196f3',
                            marginTop: '4px',
                            flexShrink: 0
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              color: event.response === 'accepted' ? '#4caf50' :
                                     event.response === 'rejected' ? '#f44336' :
                                     '#333'
                            }}>
                              {event.event}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>
                              {event.details}
                            </div>
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '3px' }}>
                              {formatTime(event.timestamp)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    No details available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeliveryAssignmentTracker;
